// 데모 계정의 오른쪽 사이드바(작물분석·에너지)를 실계정과 같은 컴포넌트로 그리기 위한 데이터.
// BE 를 타지 않고 프런트에서 만들며, 실계정 응답과 같은 필드 이름을 쓴다.
//
//   작물분석 → GET /api/dashboard/{farmId}/diagnosis/runs 의 data
//   에너지   → GET /api/dashboard/{farmId}/overview 의 data
//
// 회차 id·날짜·시각만은 지어내지 않고 데모 기록(missionLogs)에서 가져온다.
// DiagnosisRuns 에서 회차를 누르면 그 id 가 지도로 넘어가는데(setDiagnosisFocus),
// 지도는 missionLogs 에서 id 로 결과를 찾는다. 없는 id 를 넣으면 회차를 누르는 순간
// 지도가 "비행 데이터 없음" 으로 떨어진다.
//
// React 를 import 하지 않는 순수 모듈이라 테스트에서 그대로 부를 수 있다(demoAnalysis 와 같은 규칙).

import { DEMO_POLE, demoEnergyDeviceList, demoEnergyMode } from "./demoDevices";

import { DEFAULT_DEMO_CROP, buildDemoCellSummaries, normalizeDemoCrop } from "./demoAnalysis";

// 정보 탭의 "총 셀 수"가 실제 농장 값(BE /dashboard/{id}/info)이라 여기도 같은 원천을
// 써야 한다. 지도에 그려진 칸 수(gridCells)가 곧 그 값이다. 상수로 박아 두면
// 정보 탭은 50칸인데 작물분석 탭은 220칸이라고 말하는 화면이 나온다.
const FALLBACK_TOTAL_CELLS = 0;

// 병명은 실계정에서 서버가 내려주는 값이다. DiagnosisRuns 는 한국어면 nameKo 를,
// 영어면 nameEn 을 formatReadableName 으로 풀어 쓴다(CABBAGE_SOFT_ROT → Cabbage Soft Rot).
// 진단 분류는 정상 / 병해 2종 / 해충 위험 네 갈래다. "정상" 은 병해충 표가 아니라
// 위쪽 등급 줄(okCells)이 맡으므로 여기에는 나머지 세 줄만 둔다.
// 해충은 아직 샘플 이미지가 없어 항상 0 으로 나온다 — 그래도 줄은 보여 준다.
// 분류 체계를 먼저 보여 줘야 "이 밭에 해충이 없다" 와 "해충은 아직 안 본다" 가 구분된다.
// public/demo 에 해충 이미지 쌍을 넣고 demoAnalysis 의 DEMO_SAMPLES 에
// kind: "pest" 로 추가하면 이 줄이 저절로 채워진다.
// 작물마다 병해 분류가 다르다(demoAnalysis 의 샘플 세트 kind 와 맞춘다).
// 표에 늘 같은 순서(객체 키 순)로 세운다. 회차마다 줄 순서가 바뀌면 두 회차를 비교할 수 없다.
const PEST_META_BY_CROP = {
  cabbage: {
    "soft-rot": { pestId: 101, nameKo: "양배추 무름병", nameEn: "CABBAGE_SOFT_ROT" },
    sclerotinia: { pestId: 102, nameKo: "양배추 균핵병", nameEn: "CABBAGE_SCLEROTINIA" },
    pest: { pestId: 103, nameKo: "해충 위험", nameEn: "PEST_RISK" },
  },
  rice: {
    blast: { pestId: 201, nameKo: "벼 도열병", nameEn: "RICE_BLAST" },
    "sheath-blight": { pestId: 202, nameKo: "벼 잎집무늬마름병", nameEn: "RICE_SHEATH_BLIGHT" },
    pest: { pestId: 203, nameKo: "해충 위험", nameEn: "PEST_RISK" },
  },
};
const pestMetaFor = (crop) => PEST_META_BY_CROP[normalizeDemoCrop(crop)];

const pad = (value) => String(value).padStart(2, "0");
const dateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const timeLabel = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const runFromLog = (log, totalCells, pestMeta) => {
  const results = Array.isArray(log?.results) ? log.results : [];
  // 지도 칸 색을 칠할 때 쓰는 바로 그 함수다. 여기서 따로 세면 작물분석 탭이
  // "심각 2칸"이라는데 지도에는 1칸만 빨간 상황이 생긴다.
  const { cells } = buildDemoCellSummaries(results);
  const gradeOf = (cellId) => cells[cellId]?.grade ?? "OK";

  const counts = { HIGH: 0, MID: 0, OK: 0 };
  results.forEach((result) => {
    counts[gradeOf(result.cellId)] += 1;
  });

  // 병해별 집계. 칸의 등급은 그 칸에서 가장 나쁜 사진이 정하므로(대표 사진),
  // 한 칸은 대표 병해 쪽에 한 번만 센다 — 등급 칸 수 합계와 어긋나지 않는다.
  // 검출된 것만 담지 않고 분류 전체를 먼저 깔아 둔다. 실계정은 서버가 검출된 병해만
  // 내려주지만, 데모는 어떤 항목을 보는지 자체가 설명 대상이라 0 인 줄도 남긴다.
  const pestOrder = Object.keys(pestMeta);
  const byPest = new Map(
    pestOrder.map((kind) => [
      kind,
      { ...pestMeta[kind], highCells: 0, midCells: 0, cropCount: 0 },
    ])
  );
  results.forEach((result) => {
    const row = byPest.get(result.kind);
    if (!row) return; // 정상 칸
    const grade = gradeOf(result.cellId);
    if (grade === "HIGH") row.highCells += 1;
    else if (grade === "MID") row.midCells += 1;
    row.cropCount += Array.isArray(result.photos) ? result.photos.length : 0;
  });

  return {
    missionLogId: log.id,
    date: dateKey(new Date(log.completedAt)),
    time: timeLabel(new Date(log.completedAt)),
    scannedCells: results.length,
    highCells: counts.HIGH,
    midCells: counts.MID,
    okCells: counts.OK,
    unscannedCells: Math.max(0, totalCells - results.length),
    diseases: pestOrder.map((kind) => byPest.get(kind)),
  };
};

// from/to 는 RangeToolbar 가 주는 "YYYY-MM-DD". 실계정은 서버가 걸러 주므로
// 여기서도 걸러야 기간 버튼이 실제로 동작하는 것처럼 보인다(ISO 문자열이라 사전순 비교로 충분).
export const buildDemoDiagnosisRuns = (
  missionLogs,
  from,
  to,
  totalCells = FALLBACK_TOTAL_CELLS,
  crop = DEFAULT_DEMO_CROP
) => {
  const total = Math.max(0, Number(totalCells) || FALLBACK_TOTAL_CELLS);
  const pestMeta = pestMetaFor(crop);
  const logs = (Array.isArray(missionLogs) ? missionLogs : []).filter(
    (log) => log?.completedAt && Array.isArray(log.results) && log.results.length
  );

  const runs = logs
    .map((log) => runFromLog(log, total, pestMeta))
    .filter((run) => (!from || run.date >= from) && (!to || run.date <= to))
    // 최신 회차가 위로. DiagnosisRuns 는 runs[0] 을 펼친 채로 띄운다.
    .sort((a, b) => (a.date === b.date ? b.time.localeCompare(a.time) : b.date.localeCompare(a.date)));

  const shown = new Set(runs.map((run) => run.missionLogId));
  const scanned = new Set();
  logs.forEach((log) => {
    if (!shown.has(log.id)) return;
    log.results.forEach((result) => scanned.add(result.cellId));
  });

  return {
    totalCells: total,
    neverScannedCells: Math.max(0, total - scanned.size),
    // 데모는 결과 없는 회차·중단된 회차를 만들지 않는다.
    emptyRuns: 0,
    abortedRuns: 0,
    runs,
  };
};

// ── 에너지 ──
// 에너지는 지도와 이어진 값이 없어 전부 고정값이다. 날짜·시각만 부를 때 만든다 —
// 고정 ISO 를 박아 두면 "3분 전"이 며칠 뒤 "412일 전"으로 읽힌다.
//
// 응답 모양은 BE 가 앞으로 내려줄 overview.energy { mode, devices[], group } (설계노트 6장 · 안 A).
//   latest   : 지금 RealtimeStatusResponse 필드 그대로 + sharedInW / sharedOutW
//              (받음/보냄은 하드웨어 값이 오기 전까지 null — 화면은 null 이면 그 줄을 숨긴다)
//   forecast : 1시간 간격 6칸. AI 값이 오기 전까지 가짜. 비어 있으면 화면은 차트 자리를 숨긴다.
//   group    : fog 구성에서만. 합계는 BE 가 더해 주는 값이라 여기서도 더해서 넣는다.

const DEMO_DAILY_GEN = [10.8, 12.4, 11.7, 14.3, 15.6, 13.9, 16.8];
const DEMO_DAILY_CONS = [7.1, 7.8, 7.4, 8.2, 8.7, 8.1, 8.9];
// 마지막 날(오늘)은 현재 저장량과 같아야 그래프 끝점과 카드 숫자가 안 어긋난다.
const DEMO_DAILY_BATT = [73, 76, 74, 80, 84, 82, DEMO_POLE.batteryPercent];
// 오후로 갈수록 햇빛이 줄어드는 모양. 소비는 거의 일정.
const FORECAST_GEN_FACTOR = [1.0, 0.92, 0.78, 0.58, 0.36, 0.18];
const FORECAST_CONS_FACTOR = [1.0, 1.03, 0.98, 0.95, 0.92, 0.9];
// 지금 시간대까지 6시간 실측(-6h ~ 지금, 7점). 아침에서 낮으로 가며 햇빛이 늘어나는 모양.
// 마지막 점(지금)은 실시간 값과 같아야 카드 숫자와 차트 끝점이 안 어긋난다.
const HISTORY_GEN_FACTOR = [0.35, 0.5, 0.68, 0.82, 0.93, 0.98, 1.0];
const HISTORY_CONS_FACTOR = [0.9, 0.92, 0.97, 1.0, 1.02, 0.99, 1.0];
// 풍력(폴만)은 햇빛과 상관없이 흔들린다. 마지막은 1.0 — 지금 값과 같게.
const HISTORY_WIND_FACTOR = [0.8, 1.1, 0.9, 1.2, 1.0, 0.95, 1.0];
const FORECAST_WIND_FACTOR = [1.0, 0.9, 1.1, 1.0, 0.85, 0.9];
// 지난 6시간에 대해 "그때 AI 가 예측했던 값". AI 팀이 과거 예측을 보관해 주면 실측 위에 점선으로 겹쳐
// 예측이 얼마나 맞았는지 보인다. 실측에서 몇 % 벗어난 모양으로 만든다.
const PAST_FORECAST_GEN_DEV = [1.08, 0.94, 1.05, 0.97, 1.03, 0.99, 1.0];
const PAST_FORECAST_CONS_DEV = [0.96, 1.04, 0.98, 1.02, 0.99, 1.01, 1.0];

const round1 = (value) => Math.round(value * 10) / 10;
const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(value)));
const genW = (device) => device.solarW + device.windW;
const sumBy = (items, pick) => items.reduce((sum, item) => sum + (Number(pick(item)) || 0), 0);

const DAY_MS = 86400000;
const MAX_DAILY_DAYS = 31;
const parseDay = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// 기간 버튼(1일/7일/30일)이 주는 from~to 만큼 날짜를 만든다. 실계정은 서버가 기간대로 걸러 주므로
// 데모도 따라야 버튼이 실제로 동작하는 것처럼 보인다. 없으면 최근 7일.
const dayCountFor = (range, now) => {
  const from = parseDay(range?.from);
  if (!from) return DEMO_DAILY_GEN.length;
  const to = parseDay(range?.to) ?? now;
  const days = Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS) + 1;
  return Math.max(1, Math.min(MAX_DAILY_DAYS, days));
};

// 큐브 일별은 폴 시리즈를 그 장치의 발전·소비 비율로 줄인다. 배터리 오늘 값은 카드와 같게.
// 7일 패턴을 반복하되 날짜마다 조금씩 흔들어 30일에서도 같은 모양이 반복돼 보이지 않게 한다.
const dailyFor = (device, now, dayCount = DEMO_DAILY_GEN.length) => {
  const genRatio = genW(device) / genW(DEMO_POLE);
  const consRatio = device.consumptionW / DEMO_POLE.consumptionW;
  const battShift = device.batteryPercent - DEMO_POLE.batteryPercent;
  return Array.from({ length: dayCount }, (_, index) => {
    const date = new Date(now);
    date.setDate(date.getDate() - (dayCount - 1 - index));
    const base = index % DEMO_DAILY_GEN.length;
    const wobble = 1 + (((index * 7) % 5) - 2) * 0.04;
    const isLast = index === dayCount - 1;
    return {
      statDate: dateKey(date),
      totalGenKwh: round1(DEMO_DAILY_GEN[base] * genRatio * wobble),
      totalConsKwh: round1(DEMO_DAILY_CONS[base] * consRatio * wobble),
      avgBattLevel: isLast
        ? device.batteryPercent
        : clampPercent(DEMO_DAILY_BATT[base] + battShift),
    };
  });
};

// 과거 6시간(실측 시각과 같은 7점) + 다음 정시부터 1시간 간격 6칸 = 13점.
// 실계정은 AI 가 과거 예측을 보관해 줄 때 과거 점이 함께 오고, 없으면 미래 6점만 온다. 차트는 둘 다 그린다.
const forecastFor = (device, now) => {
  const past = historyFor(device, now).map((point, index) => {
    const solarW = Math.round(point.solarW * PAST_FORECAST_GEN_DEV[index]);
    const windW = Math.round(point.windW * PAST_FORECAST_GEN_DEV[index]);
    return {
      ts: point.ts,
      genW: solarW + windW,
      consW: Math.round(point.consW * PAST_FORECAST_CONS_DEV[index]),
      solarW,
      windW,
    };
  });
  const future = FORECAST_GEN_FACTOR.map((factor, index) => {
    const ts = new Date(now);
    ts.setMinutes(0, 0, 0);
    ts.setHours(ts.getHours() + index + 1);
    return splitPoint(ts, device, factor, FORECAST_WIND_FACTOR[index], FORECAST_CONS_FACTOR[index]);
  });
  return [...past, ...future];
};

// 태양광 · 풍력을 따로 두고 발전은 그 합. 폴 차트는 둘을 따로 그리고, 큐브는 풍력 0 이라 발전 한 줄.
const splitPoint = (ts, device, solarFactor, windFactor, consFactor) => {
  const solarW = Math.round(device.solarW * solarFactor);
  const windW = Math.round(device.windW * windFactor);
  return {
    ts: ts.toISOString(),
    genW: solarW + windW,
    consW: Math.round(device.consumptionW * consFactor),
    solarW,
    windW,
  };
};

// 현재 정시에서 거꾸로 6시간. ts 는 각 시간대의 시작.
const historyFor = (device, now) =>
  HISTORY_GEN_FACTOR.map((factor, index) => {
    const ts = new Date(now);
    ts.setMinutes(0, 0, 0);
    ts.setHours(ts.getHours() - (HISTORY_GEN_FACTOR.length - 1 - index));
    return splitPoint(ts, device, factor, HISTORY_WIND_FACTOR[index], HISTORY_CONS_FACTOR[index]);
  });

const sumPoints = (entries, key) =>
  entries[0][key].map((point, index) => ({
    ts: point.ts,
    genW: sumBy(entries, (entry) => entry[key][index].genW),
    consW: sumBy(entries, (entry) => entry[key][index].consW),
    solarW: sumBy(entries, (entry) => entry[key][index].solarW),
    windW: sumBy(entries, (entry) => entry[key][index].windW),
  }));

const deviceEntry = (device, now, dayCount) => ({
  deviceSn: device.serialNum,
  deviceType: device.deviceType,
  role: device.role,
  name: device.name,
  operatingStatus: device.operatingStatus,
  isConnected: device.isConnected,
  latest: {
    hasData: true,
    timestamp: new Date(now.getTime() - 3 * 60 * 1000).toISOString(),
    currentSolarW: device.solarW,
    currentWindW: device.windW,
    currentConsumptionW: device.consumptionW,
    // 정보 탭 장비 카드의 배터리와 같은 값이어야 한다.
    currentStoragePercent: device.batteryPercent,
    sharedInW: null,
    sharedOutW: null,
  },
  history: historyFor(device, now),
  forecast: forecastFor(device, now),
  daily: dailyFor(device, now, dayCount),
});

const groupOf = (entries) => {
  const totalGenW = sumBy(entries, (entry) => entry.latest.currentSolarW + entry.latest.currentWindW);
  const totalConsW = sumBy(entries, (entry) => entry.latest.currentConsumptionW);
  return {
    memberCount: entries.length,
    reportingCount: entries.length,
    totalGenW,
    totalConsW,
    surplusW: totalGenW - totalConsW,
    avgSocPercent: Math.round(sumBy(entries, (entry) => entry.latest.currentStoragePercent) / entries.length),
    history: sumPoints(entries, "history"),
    forecast: sumPoints(entries, "forecast"),
    daily: entries[0].daily.map((day, index) => ({
      statDate: day.statDate,
      totalGenKwh: round1(sumBy(entries, (entry) => entry.daily[index].totalGenKwh)),
      totalConsKwh: round1(sumBy(entries, (entry) => entry.daily[index].totalConsKwh)),
      avgBattLevel: Math.round(sumBy(entries, (entry) => entry.daily[index].avgBattLevel) / entries.length),
    })),
  };
};

// range: RangeToolbar 가 주는 { from, to } ("YYYY-MM-DD"). 일별 차트만 이 기간을 탄다.
// registered: 관리자 등록 장치(DemoScenarioContext.demoRegisteredDevices). 정보 탭·지도와 같은 장치 목록이 된다.
export const buildDemoEnergyOverview = (config, now = new Date(), range = null, registered = undefined) => {
  const mode = demoEnergyMode(config, registered);
  const dayCount = dayCountFor(range, now);
  const devices = demoEnergyDeviceList(config, registered).map((device) =>
    deviceEntry(device, now, dayCount)
  );
  return {
    energy: {
      mode,
      devices,
      group: mode === "FOG" ? groupOf(devices) : null,
    },
  };
};
